/**
 * funnelsSlice.ts — Redux slice for the Funnels resource (WEB-297 Funnel F3 FE).
 *
 * Mirrors the quizzesSlice pattern. State shape:
 * { funnels, total, isLoading, error }
 *
 * WEB-323: adds deleteFunnelThunk (soft-delete via DELETE /funnels/:id).
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { funnelsApi } from '@/api';
import { extractApiError, extractApiErrorWithTierNumbers } from '@/api/errorStatus';
import type { ApiErrorInfo } from '@/api/errorStatus';
import type { FunnelListParams } from '@/api/funnels';
import type { Funnel, FunnelsState } from '@/types';
import { preserveFunnelListOnlyFields } from './preserveOwnerName';
import { switchActiveOrgThunk } from './organizationsSlice';
import { switchActiveProjectThunk, clearActiveProjectThunk } from './projectsSlice';
import { rejectedApiError } from './rejectedApiError';

const initialState: FunnelsState = {
  funnels: [],
  total: 0,
  isLoading: false,
  error: null,
  limit: 20,
  offset: 0,
  projectId: null,
};

/**
 * WEB-745: fetchFunnelsThunk now accepts pagination/search params. Backward
 * compatible with the bare-string call (`fetchFunnelsThunk(projectId)`) —
 * that form fetches with backend defaults (limit=20, offset=0).
 */
export type FetchFunnelsArg = string | ({ projectId: string } & FunnelListParams);

function normalizeFetchArg(arg: FetchFunnelsArg): { projectId: string } & FunnelListParams {
  return typeof arg === 'string' ? { projectId: arg } : arg;
}

export const fetchFunnelsThunk = createAsyncThunk(
  'funnels/fetchAll',
  async (arg: FetchFunnelsArg, { rejectWithValue }) => {
    const { projectId, ...params } = normalizeFetchArg(arg);
    try {
      const response = await funnelsApi.list(projectId, params);
      return response.data as { data: Funnel[]; total: number };
    } catch (error: unknown) {
      return rejectWithValue(extractApiError(error));
    }
  }
);

export const deleteFunnelThunk = createAsyncThunk(
  'funnels/delete',
  async ({ projectId, id }: { projectId: string; id: string }, { rejectWithValue }) => {
    try {
      await funnelsApi.delete(projectId, id);
      return id;
    } catch (error: unknown) {
      return rejectWithValue(extractApiError(error));
    }
  },
);

// QA 2026-07-20: инлайн-переименование воронки из списка (паттерн WEB-386,
// как updateQuizThunk). PATCH возвращает обновлённую воронку целиком.
export const updateFunnelThunk = createAsyncThunk(
  'funnels/update',
  async (
    { projectId, id, data }: { projectId: string; id: string; data: { name: string } },
    { rejectWithValue },
  ) => {
    try {
      const response = await funnelsApi.update(projectId, id, data);
      return response.data as Funnel;
    } catch (error: unknown) {
      return rejectWithValue(extractApiError(error));
    }
  },
);

// WEB-416: publish/unpublish toggle for the funnels list page. Mirrors
// paywallsSlice publish/unpublish — the endpoint returns the full updated
// Funnel, which replaces the stale entry in state. publish clears
// unpublishedAt; unpublish sets it (status is not renamed — see Funnel type).
/**
 * WEB-951: реджект строкой (`error.message`) ломал вызывающего дважды — терялся
 * серверный `code`/`status`, а `.unwrap()` кидал строку, из-за чего проверка
 * `err instanceof Error` в Funnels.tsx никогда не срабатывала и серверная
 * причина отказа («Привяжите домен…», гейт юридических ссылок) схлопывалась в
 * generic-текст. Реджектим `{status,message,code}` — образец quizzesSlice/
 * paywallsSlice. Fallback для не-Error реджекта сохранён прежним.
 */
function toApiError(error: unknown, fallback: string): ApiErrorInfo {
  if (error instanceof Error) return extractApiError(error);
  return { status: null, message: fallback };
}

export const publishFunnelThunk = createAsyncThunk(
  'funnels/publish',
  async ({ projectId, id }: { projectId: string; id: string }, { rejectWithValue }) => {
    try {
      const response = await funnelsApi.publish(projectId, id);
      return response.data as Funnel;
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error, 'Failed to publish funnel'));
    }
  },
);

export const unpublishFunnelThunk = createAsyncThunk(
  'funnels/unpublish',
  async ({ projectId, id }: { projectId: string; id: string }, { rejectWithValue }) => {
    try {
      const response = await funnelsApi.unpublish(projectId, id);
      return response.data as Funnel;
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error, 'Failed to unpublish funnel'));
    }
  },
);

// WEB-415: move/copy funnel (+ cascade to its quiz/paywall pieces) to
// another project in the same org. Mirrors paywallsSlice movePaywallThunk/
// copyPaywallThunk. A 409 Conflict (PIECE_SHARED) rejects with the server
// message listing the blocking funnels — the client.ts interceptor already
// flattens the response body's `message` onto the Error.
/**
 * РЕВИЗИЯ ПАЧКИ 23 (находка 3) — ПОЧЕМУ ОТКАЗ БОЛЬШЕ НЕ ГОЛАЯ СТРОКА.
 *
 * Плоская строка убивала статус и код (шрам WEB-391/WEB-135): страница
 * получала текст и не могла отличить отказ ПО ТАРИФУ (402 TIER_LIMIT —
 * лимит считается по организации целиком, поэтому копия воронки в соседний
 * проект в него упирается) от обычной поломки. `asTierLimitRejection` судит
 * ровно по `status`/`code` и на строке всегда возвращала null — то есть
 * тарифная ветка страницы была мертва по построению.
 *
 * Форма ответа — общий разборщик `extractApiErrorWithTierNumbers`
 * (`api/errorStatus.ts`), один на воронки и квизы: рядом со статусом и кодом
 * сохраняются числа `usage`/`limit`, иначе окно покупки не сможет сказать
 * «использовано N из M». Своей редакции правила тут не заводится (WEB-1844).
 * `message` при этом остаётся тем же серверным текстом, который читает
 * `extractApiError` + `localizeError` на странице — путь показа причины не
 * меняется.
 */
export const moveFunnelThunk = createAsyncThunk(
  'funnels/move',
  async (
    { projectId, id, targetProjectId }: { projectId: string; id: string; targetProjectId: string },
    { rejectWithValue },
  ) => {
    try {
      await funnelsApi.move(projectId, id, targetProjectId);
      return id;
    } catch (error: unknown) {
      return rejectWithValue(extractApiErrorWithTierNumbers(error, 'Failed to move funnel'));
    }
  },
);

export const copyFunnelThunk = createAsyncThunk(
  'funnels/copy',
  async (
    { projectId, id, targetProjectId }: { projectId: string; id: string; targetProjectId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await funnelsApi.copy(projectId, id, targetProjectId);
      return response.data as Funnel;
    } catch (error: unknown) {
      return rejectWithValue(extractApiErrorWithTierNumbers(error, 'Failed to copy funnel'));
    }
  },
);

const funnelsSlice = createSlice({
  name: 'funnels',
  initialState,
  reducers: {
    clearFunnels(state) {
      state.funnels = [];
      state.total = 0;
      state.error = null;
      // WEB-1368 п.1.4: сброс снимает и привязку к проекту — иначе следующая
      // загрузка того же проекта считалась бы «уже текущей».
      state.projectId = null;
      // Ревизия пачки 37 (С1): запрос, который был в полёте, после сброса
      // будет отброшен по строгой привязке — снять прелоадер больше некому.
      state.isLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      /**
       * WEB-1368 п.1.4 — список воронок принадлежит ПРОЕКТУ, а не экрану.
       *
       * Раньше ответ писался безусловно: при быстром переключении проектов
       * опоздавший ответ прежнего проекта дорисовывался поверх нового, и
       * человек видел чужие воронки до следующей загрузки. Для смены
       * организации это уже чинили явным сбросом (WEB-1058), для смены
       * проекта через switchActiveProjectThunk — WEB-1337 P1; но обе двери
       * закрывают только СБРОС, а не гонку ответов, и ни одна не срабатывает
       * на локальной подстановке проекта (setCurrentProject).
       *
       * Здесь оба конца сразу: старт загрузки ДРУГОГО проекта чистит список,
       * а ответ и отказ по не-текущему проекту игнорируются.
       *
       * РЕВИЗИЯ ПАЧКИ 37 (С1): сравнение СТРОГОЕ. Прежнее
       * `state.projectId != null && …` самоотключалось любым сбросом — все
       * двери сброса ниже ставят `projectId = null`, и опоздавший ответ по
       * прежнему проекту снова заполнял только что очищенный список (выход
       * «во всю организацию» при запросе в полёте). После `pending`
       * привязка стоит всегда, поэтому `null` здесь значит «список ничей»,
       * а не «подойдёт любой». Оборотная сторона: отброшенный ответ больше
       * некому досказать — поэтому КАЖДАЯ дверь сброса гасит `isLoading`
       * сама, иначе экран остался бы в вечном прелоадере.
       */
      .addCase(fetchFunnelsThunk.pending, (state, action) => {
        const { projectId } = normalizeFetchArg(action.meta.arg);
        if (state.projectId !== projectId) {
          state.funnels = initialState.funnels;
          state.total = initialState.total;
        }
        state.projectId = projectId;
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFunnelsThunk.fulfilled, (state, action) => {
        const { projectId, limit, offset } = normalizeFetchArg(action.meta.arg);
        if (state.projectId !== projectId) return;
        state.projectId = projectId;
        state.isLoading = false;
        state.funnels = action.payload.data;
        state.total = action.payload.total;
        state.error = null;
        state.limit = limit ?? 20;
        state.offset = offset ?? 0;
      })
      .addCase(fetchFunnelsThunk.rejected, (state, action) => {
        const { projectId } = normalizeFetchArg(action.meta.arg);
        if (state.projectId !== projectId) return;
        state.isLoading = false;
        state.error = rejectedApiError(action);
      })
      .addCase(deleteFunnelThunk.fulfilled, (state, action) => {
        state.funnels = state.funnels.filter((f) => f.id !== action.payload);
        state.total = Math.max(0, state.total - 1);
      })
      // WEB-1074 п.2: удаление/переименование/публикация/отмена публикации —
      // отказ ОДНОЙ операции, не отказ ЗАГРУЗКИ списка. Раньше он писался в
      // то же state.error, что fetchFunnelsThunk — а Funnels.tsx передаёт
      // это поле в FunnelList как isError, который в DataTable приоритетнее
      // isLoading/empty (FunnelList.tsx: "error > loading > empty > idle").
      // Итог — отказ публикации ложно ронял ВЕСЬ список в состояние «Не
      // удалось загрузить воронки», хотя данные были на месте (живой прогон
      // 05.08, дорожка L5). Причина отказа не теряется — страница показывает
      // её тостом сама (handlePublish/handleUnpublish/confirmDelete/
      // commitRename — все читают `.unwrap()` и делают message.error), так
      // что здесь достаточно НЕ трогать общий error. errorTitle:
      // "Не удалось загрузить воронки" остаётся только за fetchFunnelsThunk.
      .addCase(deleteFunnelThunk.rejected, () => {})
      .addCase(updateFunnelThunk.fulfilled, (state, action) => {
        // Шрам WEB-735: мутационный ответ приходит с ownerName:null —
        // подмена строки целиком стёрла бы владельца.
        state.funnels = state.funnels.map((f) =>
          f.id === action.payload.id ? preserveFunnelListOnlyFields(f, action.payload) : f
        );
      })
      .addCase(updateFunnelThunk.rejected, () => {})
      .addCase(publishFunnelThunk.fulfilled, (state, action) => {
        state.funnels = state.funnels.map((f) =>
          f.id === action.payload.id ? preserveFunnelListOnlyFields(f, action.payload) : f
        );
      })
      .addCase(publishFunnelThunk.rejected, () => {})
      .addCase(unpublishFunnelThunk.fulfilled, (state, action) => {
        state.funnels = state.funnels.map((f) =>
          f.id === action.payload.id ? preserveFunnelListOnlyFields(f, action.payload) : f
        );
      })
      .addCase(unpublishFunnelThunk.rejected, () => {})
      .addCase(moveFunnelThunk.fulfilled, (state, action) => {
        state.funnels = state.funnels.filter((f) => f.id !== action.payload);
        state.total = Math.max(0, state.total - 1);
      })
      .addCase(moveFunnelThunk.rejected, () => {})
      .addCase(copyFunnelThunk.rejected, () => {})
      // WEB-1058: список воронок относится к ПРОЕКТУ, но проект живёт внутри
      // организации — переключение активной организации делает старый список
      // (и его total/пагинацию) чужим контекстом. Раньше ничто не реагировало
      // на switchActiveOrgThunk, и Контент → Воронки показывал воронки
      // прежней организации, пока страницу не перезагружали (F5). Единая
      // точка реакции — сам слайс (мирроит funnelsSlice.clearFunnels), а не
      // dispatch по каждому месту вызова переключения организации.
      .addCase(switchActiveOrgThunk.fulfilled, (state) => {
        state.funnels = initialState.funnels;
        state.total = initialState.total;
        state.error = initialState.error;
        state.limit = initialState.limit;
        state.offset = initialState.offset;
        state.projectId = initialState.projectId;
        // Ревизия пачки 37 (С1): см. clearFunnels — отброшенный ответ
        // прелоадер уже не снимет.
        state.isLoading = initialState.isLoading;
      })
      // WEB-1337 P1: same reasoning as switchActiveOrgThunk.fulfilled above,
      // but for switching PROJECT within the same org (or entering
      // org-mode) — the funnel list is scoped to a project, so a stale list
      // from the PREVIOUS project must not survive either transition.
      .addCase(switchActiveProjectThunk.fulfilled, (state) => {
        state.funnels = initialState.funnels;
        state.total = initialState.total;
        state.error = initialState.error;
        state.limit = initialState.limit;
        state.offset = initialState.offset;
        state.projectId = initialState.projectId;
        // Ревизия пачки 37 (С1): см. clearFunnels — отброшенный ответ
        // прелоадер уже не снимет.
        state.isLoading = initialState.isLoading;
      })
      .addCase(clearActiveProjectThunk.fulfilled, (state) => {
        state.funnels = initialState.funnels;
        state.total = initialState.total;
        state.error = initialState.error;
        state.limit = initialState.limit;
        state.offset = initialState.offset;
        state.projectId = initialState.projectId;
        // Ревизия пачки 37 (С1): см. clearFunnels — отброшенный ответ
        // прелоадер уже не снимет.
        state.isLoading = initialState.isLoading;
      });
    // copyFunnelThunk.fulfilled: no state change — the copy lands in another project.
  },
});

export const { clearFunnels } = funnelsSlice.actions;
export default funnelsSlice.reducer;
